import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Grid, Card, CardContent, Box, TextField, Button,
    Stack, List, ListItem, ListItemText, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { api } from '../api';

const FacebookMonitoringPage = ({ refreshData }) => {
    const [pages, setPages] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFetching, setIsFetching] = useState(null); // Tracks the ID of the item being fetched

    // State for the "Add Page" Dialog
    const [pageDialogOpen, setPageDialogOpen] = useState(false);
    const [newPage, setNewPage] = useState({ name: '', pageId: '', keywords: '' });

    // State for the "Add Group" Dialog
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [newGroup, setNewGroup] = useState({ name: '', groupUrl: '', keywords: '' });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [pagesRes, groupsRes] = await Promise.all([
                api.get('/meta/pages'),
                api.get('/meta/groups')
            ]);
            setPages(pagesRes.data);
            setGroups(groupsRes.data);
        } catch (err) {
            setError('Failed to load saved pages and groups.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // --- Page Handlers ---
    const handleAddPage = async () => {
        if (!newPage.name || !newPage.pageId) return alert('Page Name and Page ID are required.');
        try {
            await api.post('/meta/pages', newPage);
            setPageDialogOpen(false);
            setNewPage({ name: '', pageId: '', keywords: '' });
            await fetchData();
        } catch (err) { alert('Failed to add page.'); }
    };

    const handleDeletePage = async (id) => {
        if (window.confirm('Are you sure?')) {
            try {
                await api.delete(`/meta/pages/${id}`);
                await fetchData();
            } catch (err) { alert('Failed to delete page.'); }
        }
    };

    const handleFetchPage = async (id) => {
        setIsFetching(`page-${id}`);
        try {
            // This route now fetches AND saves.
            const response = await api.post(`/meta/pages/${id}/fetch`);
            alert(`Found and saved ${response.data.length} new posts/comments. Refreshing main data...`);
            await refreshData(); // Refresh all app data
        } catch (err) {
            alert('Failed to fetch page data. Ensure Page Access Token is configured.');
        } finally {
            setIsFetching(null);
        }
    };

    // --- Group Handlers ---
    const handleAddGroup = async () => {
        if (!newGroup.name || !newGroup.groupUrl) return alert('Group Name and Group URL are required.');
        try {
            await api.post('/meta/groups', newGroup);
            setGroupDialogOpen(false);
            setNewGroup({ name: '', groupUrl: '', keywords: '' });
            await fetchData();
        } catch (err) { alert('Failed to add group.'); }
    };

    const handleDeleteGroup = async (id) => {
        if (window.confirm('Are you sure?')) {
            try {
                await api.delete(`/meta/groups/${id}`);
                await fetchData();
            } catch (err) { alert('Failed to delete group.'); }
        }
    };
    
    // --- THIS IS THE FIX ---
    const handleFetchGroup = async (id) => {
        setIsFetching(`group-${id}`);
        try {
            // 1. Call the backend route that now fetches AND saves.
            const response = await api.post(`/meta/groups/${id}/fetch`);
            
            // 2. The failing '/api/mentions/batch' call has been REMOVED.
            
            alert(`Found and saved ${response.data.length} new posts. Refreshing main data...`);
            await refreshData(); // Refresh all app data
        } catch (err) {
            alert('Failed to fetch group data. Ensure Apify Token is configured.');
            console.log(err);
        } finally {
            setIsFetching(null);
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Container maxWidth="xl">
            <Typography variant="h4" gutterBottom>Facebook Monitoring</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <Grid container spacing={3}>
                {/* Managed Pages Column */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Managed Pages (Graph API)</Typography>
                                <Button startIcon={<AddCircleOutlineIcon />} onClick={() => setPageDialogOpen(true)}>Add Page</Button>
                            </Box>
                            <List>
                                {pages.map(page => (
                                    <ListItem key={page.id} divider secondaryAction={
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="outlined" startIcon={<RefreshIcon/>} onClick={() => handleFetchPage(page.id)} disabled={!!isFetching}>
                                                {isFetching === `page-${page.id}` ? '...' : 'Fetch'}
                                            </Button>
                                            <IconButton onClick={() => handleDeletePage(page.id)}><DeleteIcon /></IconButton>
                                        </Stack>
                                    }>
                                        <ListItemText primary={page.name} secondary={`Keywords: ${page.keywords || 'Any'}`} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Public Groups Column */}
                <Grid item xs={12} md={6}>
                     <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Public Groups (Apify)</Typography>
                                <Button startIcon={<AddCircleOutlineIcon />} onClick={() => setGroupDialogOpen(true)}>Add Group</Button>
                            </Box>
                             <List>
                                {groups.map(group => (
                                    <ListItem key={group.id} divider secondaryAction={
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="outlined" startIcon={<RefreshIcon/>} onClick={() => handleFetchGroup(group.id)} disabled={!!isFetching}>
                                                 {isFetching === `group-${group.id}` ? '...' : 'Fetch'}
                                            </Button>
                                            <IconButton onClick={() => handleDeleteGroup(group.id)}><DeleteIcon /></IconButton>
                                        </Stack>
                                    }>
                                        <ListItemText primary={group.name} secondary={`Keywords: ${group.keywords || 'Any'}`} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Add Page Dialog */}
            <Dialog open={pageDialogOpen} onClose={() => setPageDialogOpen(false)} fullWidth>
                <DialogTitle>Add Managed Facebook Page</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField autoFocus label="Page Name" value={newPage.name} onChange={e => setNewPage({...newPage, name: e.target.value})} />
                        <TextField label="Facebook Page ID" value={newPage.pageId} onChange={e => setNewPage({...newPage, pageId: e.target.value})} />
                        <TextField label="Keywords (comma-separated)" value={newPage.keywords} onChange={e => setNewPage({...newPage, keywords: e.target.value})} helperText="Optional. If blank, all posts will be fetched." />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPageDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddPage} variant="contained">Save Page</Button>
                </DialogActions>
            </Dialog>

            {/* Add Group Dialog */}
            <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} fullWidth>
                <DialogTitle>Add Public Facebook Group</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField autoFocus label="Group Name" value={newGroup.name} onChange={e => setNewGroup({...newGroup, name: e.target.value})} />
                        <TextField label="Facebook Group URL" value={newGroup.groupUrl} onChange={e => setNewGroup({...newGroup, groupUrl: e.target.value})} />
                        <TextField label="Keywords (comma-separated)" value={newGroup.keywords} onChange={e => setNewGroup({...newGroup, keywords: e.target.value})} helperText="Optional. If blank, all posts will be fetched." />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddGroup} variant="contained">Save Group</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default FacebookMonitoringPage;